import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getAuthSession,
  SHOWCOMBOOFFERS,
  SUCCESS,
  getPreUrlAccordingToCart
} from "../../constants/constant";
import { snpAxios } from "../global/api";
import "./GlobalRestoProductSearch.css";

const GlobalComboSearch = ({ open, onClose, onSelect, baseUrl, shopHash, cartType }) => {
  const [query, setQuery] = useState("");
  const [combos, setCombos] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setQuery("");
    setActiveIndex(0);
    setLoading(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);

    snpAxios.post(`${baseUrl}${getPreUrlAccordingToCart(cartType)}${SHOWCOMBOOFFERS}`, {
      jcJson: {
        apiToken: getAuthSession().apiToken,
        shopHash,
        v2: true,
      },
    }).then((response) => {
      if (active) setCombos(response.data.status === SUCCESS ? response.data.jcJson.value_2 || [] : []);
    }).catch((error) => {
      if (active) {
        console.error("Global combo search failed:", error);
        setCombos([]);
      }
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => { active = false; };
  }, [baseUrl, cartType, open, shopHash]);

  const filteredCombos = useMemo(() => {
    const search = query.trim().toLocaleLowerCase();
    if (!search) return combos;
    return combos.filter((combo) => combo.comboName?.toLocaleLowerCase().includes(search));
  }, [combos, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    resultsRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (!open) return null;

  const selectCombo = (combo) => {
    if (!combo) return;
    onClose();
    onSelect(combo);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    } else if (event.key === "ArrowDown" && filteredCombos.length) {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % filteredCombos.length);
    } else if (event.key === "ArrowUp" && filteredCombos.length) {
      event.preventDefault();
      setActiveIndex((current) => (current - 1 + filteredCombos.length) % filteredCombos.length);
    } else if (event.key === "Enter" && filteredCombos.length) {
      event.preventDefault();
      selectCombo(filteredCombos[activeIndex]);
    }
  };

  return (
    <div className="global-resto-search" role="presentation" onMouseDown={onClose}>
      <section
        aria-label="Search combo offers"
        aria-modal="true"
        className="global-resto-search__panel"
        onKeyDown={handleKeyDown}
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
      >
        <div className="global-resto-search__heading">
          <div><span>Restaurant offers</span><h2>Find and edit a combo</h2></div>
          <kbd>Esc</kbd>
        </div>
        <div className="global-resto-search__input-wrap">
          <span aria-hidden="true">⌕</span>
          <input
            ref={inputRef}
            aria-label="Search combo name"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search combo name…"
            value={query}
          />
          {loading ? <small>Loading…</small> : null}
        </div>
        <div ref={resultsRef} className="global-resto-search__results" role="listbox">
          {!loading && !filteredCombos.length ? (
            <div className="global-resto-search__hint">No combo offers found.</div>
          ) : filteredCombos.map((combo, index) => (
            <button
              aria-selected={index === activeIndex}
              className={index === activeIndex ? "is-active" : ""}
              key={combo.id}
              onClick={() => selectCombo(combo)}
              onMouseEnter={() => setActiveIndex(index)}
              role="option"
              type="button"
            >
              <span className="global-resto-search__item-copy">
                <strong>{combo.comboName}</strong>
                <small>{combo.statusId}</small>
              </span>
              <span className="global-resto-search__rate">₹{combo.finalAmount}</span>
            </button>
          ))}
        </div>
        <footer>
          <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
          <span><kbd>Enter</kbd> edit combo</span>
        </footer>
      </section>
    </div>
  );
};

export default GlobalComboSearch;
